import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  infaqService,
  DonationCampaign,
  FundAllocation,
} from '../../../services/infaqService';
import LoadingSpinner from '../../../components/LoadingSpinner';
import MultiFileUpload from '../../../components/admin/MultiFileUpload';

const CATEGORY_SUGGESTIONS = [
  'Pemakaman',
  'Bantuan Keluarga',
  'Logistik',
  'Pengurusan Jenazah',
  'Pembersihan',
  'Penyelenggaraan',
  'Lain-lain',
];

function InfaqAllocateFundsFormPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [campaign, setCampaign] = useState<DonationCampaign | null>(null);
  const [allocations, setAllocations] = useState<FundAllocation[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    category: '',
    description: '',
    amount: 0,
    notes: '',
  });
  const [receipts, setReceipts] = useState<string[]>([]);
  const [invoices, setInvoices] = useState<string[]>([]);
  const [images, setImages] = useState<string[]>([]);
  const [uploadsInProgress, setUploadsInProgress] = useState(0);

  useEffect(() => {
    if (campaignId) {
      fetchCampaign(campaignId);
    }
    window.scrollTo(0, 0);
  }, [campaignId]);

  const fetchCampaign = async (id: string) => {
    try {
      const campaigns = await infaqService.getAllCampaigns();
      const found = campaigns.find((c) => c.id === id);
      if (!found) {
        setError('Kempen tidak dijumpai');
        setLoading(false);
        return;
      }
      setCampaign(found);
      // Fetch detailed campaign (with fundAllocations) via the public slug endpoint
      try {
        const detailed = await infaqService.getCampaignBySlug(found.slug);
        setAllocations(detailed.fundAllocations || []);
      } catch {
        // Non-fatal — allocations list is contextual only
      }
    } catch (err: any) {
      setError(err.message || 'Gagal memuat kempen');
    } finally {
      setLoading(false);
    }
  };

  const availableFunds =
    campaign != null
      ? Number(campaign.currentAmount) - Number(campaign.utilizedAmount)
      : 0;

  const handleUploadBusy = (delta: number) => {
    setUploadsInProgress((prev) => Math.max(0, prev + delta));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!campaignId || !campaign) return;

    // Client-side validation
    if (!formData.category.trim()) {
      setError('Kategori diperlukan');
      return;
    }
    if (!formData.description.trim()) {
      setError('Penerangan diperlukan');
      return;
    }
    if (!formData.amount || formData.amount <= 0) {
      setError('Jumlah mesti lebih daripada 0');
      return;
    }
    if (formData.amount > availableFunds) {
      setError(
        `Jumlah melebihi dana tersedia (${infaqService.formatCurrency(availableFunds)})`,
      );
      return;
    }
    if (uploadsInProgress > 0) {
      setError('Tunggu sehingga semua fail selesai dimuat naik');
      return;
    }

    try {
      setSubmitting(true);
      await infaqService.allocateFunds(campaignId, {
        category: formData.category.trim(),
        description: formData.description.trim(),
        amount: Number(formData.amount),
        receipts: receipts.length ? receipts : undefined,
        invoices: invoices.length ? invoices : undefined,
        images: images.length ? images : undefined,
        notes: formData.notes.trim() || undefined,
      });
      toast.success('Peruntukan dana berjaya direkodkan');
      navigate('/admin/infaq/campaigns');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || 'Gagal merakam peruntukan dana';
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-neutral-50 py-8">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/admin/infaq/campaigns" className="inline-flex items-center gap-2 text-[#0D7330] hover:underline mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali ke Senarai Kempen
          </Link>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error || 'Kempen tidak dijumpai'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin/infaq/campaigns" className="inline-flex items-center gap-2 text-[#0D7330] hover:underline mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali ke Senarai Kempen
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Peruntukkan Dana</h1>
          <p className="text-gray-600 mt-1">{campaign.title}</p>
        </div>

        {/* Summary card */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Dikumpul</p>
              <p className="text-lg font-semibold text-[#0D7330]">
                {infaqService.formatCurrency(Number(campaign.currentAmount))}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Digunakan</p>
              <p className="text-lg font-semibold text-orange-600">
                {infaqService.formatCurrency(Number(campaign.utilizedAmount))}
              </p>
            </div>
            <div className="col-span-2">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Dana Tersedia</p>
              <p className="text-lg font-semibold text-gray-900">
                {infaqService.formatCurrency(availableFunds)}
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8">
          <div className="space-y-6">
            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kategori *
              </label>
              <input
                type="text"
                list="category-suggestions"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Contoh: Pemakaman"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
              <datalist id="category-suggestions">
                {CATEGORY_SUGGESTIONS.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <p className="text-xs text-gray-500 mt-1">Pilih atau taip kategori tersendiri</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Penerangan *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Terangkan bagaimana dana ini digunakan..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jumlah (RM) *
              </label>
              <input
                type="number"
                value={formData.amount || ''}
                onChange={(e) =>
                  setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })
                }
                min="0.01"
                max={availableFunds}
                step="0.01"
                placeholder="0.00"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Maksimum: {infaqService.formatCurrency(availableFunds)}
              </p>
            </div>

            {/* Receipts */}
            <MultiFileUpload
              label="Resit"
              urls={receipts}
              onUrlsChange={setReceipts}
              onUploadingChange={(busy) => handleUploadBusy(busy ? 1 : -1)}
              helperText="Lampirkan resit sebagai bukti perbelanjaan"
            />

            {/* Invoices */}
            <MultiFileUpload
              label="Invois"
              urls={invoices}
              onUrlsChange={setInvoices}
              onUploadingChange={(busy) => handleUploadBusy(busy ? 1 : -1)}
              helperText="Invois dari pembekal (jika ada)"
            />

            {/* Images */}
            <MultiFileUpload
              label="Gambar"
              urls={images}
              onUrlsChange={setImages}
              onUploadingChange={(busy) => handleUploadBusy(busy ? 1 : -1)}
              helperText="Gambar bukti perbelanjaan / aktiviti"
            />

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nota
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Nota tambahan (pilihan)..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 mt-8 pt-8 border-t border-gray-200">
            <button
              type="submit"
              disabled={submitting || uploadsInProgress > 0}
              className="px-6 py-3 bg-[#0D7330] text-white font-semibold rounded-lg hover:bg-[#0a5c26] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Menyimpan...' : 'Simpan Peruntukan'}
            </button>
            <Link
              to="/admin/infaq/campaigns"
              className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Batal
            </Link>
            {uploadsInProgress > 0 && (
              <span className="text-sm text-gray-500">Menunggu muat naik fail...</span>
            )}
          </div>
        </form>

        {/* Existing allocations — contextual history */}
        {allocations.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Sejarah Penggunaan Dana</h2>
            <ul className="divide-y divide-gray-100">
              {allocations.map((a) => (
                <li key={a.id} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-[#0D7330]">
                        {a.category}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(a.utilizationDate).toLocaleDateString('ms-MY', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{a.description}</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 shrink-0">
                    {infaqService.formatCurrency(Number(a.amount))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

export default InfaqAllocateFundsFormPage;
